import { useState } from "react"
import { useOutletContext } from "react-router-dom"
import ToolShell from "../components/ToolShell"
import CTABox from "../components/CTABox"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"
import { ShieldCheck, UserCheck, CheckCircle2, XCircle, AlertCircle } from "lucide-react"

export default function DebtToIncomeCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [mode, setMode] = useState<"landlord" | "tenant">("landlord")

  // Landlord Mode State
  const [grossIncome, setGrossIncome] = useState(6000)
  const [existingRental, setExistingRental] = useState(0)
  const [newRental, setNewRental] = useState(1500)
  const [existingDebts, setExistingDebts] = useState(1200)
  const [newMortgage, setNewMortgage] = useState(1100)

  // Tenant Mode State
  const [targetRent, setTargetRent] = useState(1500)
  const [tenantIncome, setTenantIncome] = useState(4500)
  const [tenantDebts, setTenantDebts] = useState(600)

  // Landlord Calcs
  const totalLandlordIncome = grossIncome + existingRental + newRental
  const totalLandlordDebt = existingDebts + newMortgage
  const backEndDTI = totalLandlordIncome > 0 ? (totalLandlordDebt / totalLandlordIncome) * 100 : 0
  const frontEndDTI = grossIncome > 0 ? (newMortgage / grossIncome) * 100 : 0

  let landlordQualification = ""
  let landlordAlertColors = "text-green-700 bg-green-50 border-green-200"
  if (backEndDTI < 36) {
    landlordQualification = "Excellent — most lenders will approve strongly"
  } else if (backEndDTI <= 43) {
    landlordQualification = "Good — within typical lender limits for investment property"
    landlordAlertColors = "text-teal-700 bg-teal-50 border-teal-200"
  } else if (backEndDTI <= 50) {
    landlordQualification = "Borderline — some lenders may decline or require higher downpayment"
    landlordAlertColors = "text-amber-700 bg-amber-50 border-amber-200"
  } else {
    landlordQualification = "Unlikely — exceeds most lender risk thresholds"
    landlordAlertColors = "text-rose-700 bg-rose-50 border-rose-200"
  }

  // Tenant Calcs
  const rentToIncomeRatio = tenantIncome > 0 ? (targetRent / tenantIncome) * 100 : 0
  const totalTenantDTI = tenantIncome > 0 ? ((targetRent + tenantDebts) / tenantIncome) * 100 : 0
  const requiredIncome3x = targetRent * 3
  const incomeGap = Math.max(0, requiredIncome3x - tenantIncome)
  const tenantPasses = rentToIncomeRatio <= 33.3

  return (
    <ToolShell>
      {/* Mode Tabs */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setMode("landlord")}
          className={`flex-1 flex items-center justify-center py-4 px-2 font-medium transition-colors border-b-2 ${
            mode === "landlord"
              ? "border-primary text-primary bg-primary/5"
              : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
          }`}
        >
          <ShieldCheck className="w-5 h-5 mr-2" />
          Landlord (Borrowing)
        </button>
        <button
          onClick={() => setMode("tenant")}
          className={`flex-1 flex items-center justify-center py-4 px-2 font-medium transition-colors border-b-2 ${
            mode === "tenant"
              ? "border-primary text-primary bg-primary/5"
              : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
          }`}
        >
          <UserCheck className="w-5 h-5 mr-2" />
          Tenant (Screening)
        </button>
      </div>

      <div className="p-6 sm:p-8">
        {mode === "landlord" ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-800 border-b border-slate-100 pb-2">Income Sources</h4>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Gross Monthly Income (Job/Salary)</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={grossIncome || ''} onChange={(e) => setGrossIncome(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Existing Rental Income</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={existingRental || ''} onChange={(e) => setExistingRental(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Projected Rental Income (New Property)</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={newRental || ''} onChange={(e) => setNewRental(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                </div>
              </div>
              
              <h4 className="font-semibold text-slate-800 border-b border-slate-100 pb-2 mt-6">Debt Obligations</h4>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Existing Monthly Debts (Loans, Cards, Mortgages)</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={existingDebts || ''} onChange={(e) => setExistingDebts(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Projected New Mortgage</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={newMortgage || ''} onChange={(e) => setNewMortgage(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl flex flex-col items-center">
                <span className="text-sm font-medium text-slate-500 mb-2">Back-End DTI (Total Debt / Total Income)</span>
                <span className="text-6xl font-extrabold text-slate-900 tracking-tight">{backEndDTI.toFixed(1)}%</span>
                <div className={`mt-6 w-full p-4 rounded-xl border flex items-start gap-3 ${landlordAlertColors}`}>
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium leading-relaxed">{landlordQualification}</span>
                </div>
              </div>

              <div className="bg-white border border-slate-200 p-6 rounded-2xl">
                <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-3 mb-3 text-slate-600">
                  <span>Front-End DTI (New Mortgage / Personal Income)</span>
                  <span className="font-bold text-slate-900">{frontEndDTI.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-3 mb-3 text-slate-600">
                  <span>Total Calculated Debt</span>
                  <span className="font-medium text-slate-900">{currency}{totalLandlordDebt}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-slate-600">
                  <span>Total Calculated Income</span>
                  <span className="font-medium text-slate-900">{currency}{totalLandlordIncome}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-800 border-b border-slate-100 pb-2">Applicant Profile</h4>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Target Monthly Rent</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={targetRent || ''} onChange={(e) => setTargetRent(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tenant Gross Monthly Income</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={tenantIncome || ''} onChange={(e) => setTenantIncome(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tenant Existing Minimum Debt Payments</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={tenantDebts || ''} onChange={(e) => setTenantDebts(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                </div>
                <p className="text-xs text-slate-500 mt-1">Car loans, cards, child support, etc. Do not include current rent.</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className={`border p-6 rounded-2xl flex flex-col items-center ${tenantPasses ? 'bg-green-50 border-green-200' : 'bg-rose-50 border-rose-200'}`}>
                {tenantPasses ? <CheckCircle2 className="w-12 h-12 text-green-600 mb-2" /> : <XCircle className="w-12 h-12 text-rose-600 mb-2" />}
                <span className="text-sm font-medium text-slate-500 mb-1">{tenantPasses ? 'Tenant Meets Income Criteria' : 'Tenant Fails Income Criteria'}</span>
                <span className={`text-5xl font-extrabold tracking-tight ${tenantPasses ? 'text-green-700' : 'text-rose-700'}`}>{rentToIncomeRatio.toFixed(1)}%</span>
                <span className="text-sm text-slate-500 mt-2">Rent to Income Ratio (Ideal ≤ 30%)</span>
              </div>

              <div className="bg-white border text-sm border-slate-200 p-6 rounded-2xl">
                <div className="flex justify-between items-center text-slate-600 mb-3">
                  <span>3x Rent Income Requirement</span>
                  <span className="font-medium text-slate-900">{currency}{requiredIncome3x} /mo</span>
                </div>
                {!tenantPasses && (
                  <div className="flex justify-between items-center text-rose-600 mb-3 p-2 bg-rose-50 rounded">
                    <span>Income Shortfall</span>
                    <span className="font-bold">{currency}{incomeGap} /mo</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-slate-600 border-t border-slate-100 pt-3">
                  <span>Total Debt Load (Rent + Debt) / Income</span>
                  <span className={`font-medium ${totalTenantDTI > 45 ? 'text-amber-600' : 'text-slate-900'}`}>{totalTenantDTI.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="px-6 sm:px-8 pb-10 mt-4 border-t border-slate-100 pt-10">
        <FormulaBox 
          title="Debt-to-Income Formulas"
          formula={`Landlord DTI = (Total Existing Debt + New Mortgage) / (Personal Income + Total Rental Income)\n\nTenant Rent-to-Income = (Monthly Rent / Tenant Gross Monthly Income)\nTenant Total DTI = (Rent + Tenant Debts) / Tenant Gross Monthly Income`} 
        />
        <CTABox icon={tool.icon} heading="Manage tenant applications digitally" body={tool.ctaText} ctaLabel="Add your first property" ctaHref="/register" />
        <ToolFAQ 
          items={[
            { q: "What is Back-End vs Front-End DTI?", a: "Front-end DTI only compares your housing payment (mortgage) to your income. Back-end DTI includes all required monthly debt payments (credit cards, cars, combined housing). Lenders prioritize the Back-End DTI." },
            { q: "Can I count projected rental income?", a: "Usually yes! Lenders typically allow you to use 75% of the projected rental income from the property you are buying to help offset the DTI impact of the new mortgage." },
            { q: "Why is the tenant threshold 30%?", a: "The standard affordability rule of thumb is that housing costs should not exceed 30% of gross income, which translates directly to requiring the tenant's gross income to be at least roughly 3 times the rent." }
          ]} 
        />
        <RelatedToolsList relatedSlugs={tool.related} />
      </div>
    </ToolShell>
  )
}
